import React from 'react';
import PropTypes from 'prop-types';
import { Grid, Tooltip, Checkbox, FormControlLabel, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import FormulaField from 'features/common/components/formula-field';
import { FormulaFieldContainer, FormulaFieldLabel } from 'components/common/Styles';
import { Autocomplete, Button, TextField, Typography } from '@totalsoft/rocket-ui';

const EngineAttributeComponent = ({
  engineAttribute,
  dispatch,
  validation,
  onTestAttribute,
  attributeTestResult,
  scoringGridsForAttributeTesting
}) => {
  const { t } = useTranslation();
  const onPropertyChanged = (propertyName, propertyValue) => {
    dispatch({ type: 'OnPropertyChanged', propertyName, propertyValue });
  };

  const onAttributeTestingFieldsChanged = (propertyName, propertyValue) => {
    dispatch({ type: 'OnAttributeTestingFieldsChanged', propertyName, propertyValue });
  };

  return (
    <>
      <Prompt when={engineAttribute.changed} message={t('EngineAttributes.LeavingWithoutSavingEngineAttribute')} />
      <Grid container spacing={2} alignItems='center'>
        <Grid item xs={12} sm={4}>
          <TextField
            isNumeric
            fullWidth
            value={engineAttribute.orderNo}
            decimalScale={0}
            label={t('EngineAttributes.Details.OrderNo')}
            onChange={event => onPropertyChanged('orderNo', event)}
            error={!isValid(validation.orderNo)}
            helperText={getErrors(validation.orderNo)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            value={engineAttribute.attributeName || ''}
            onChange={event => onPropertyChanged('attributeName', event)}
            label={t('EngineAttributes.Details.AttributeName')}
            error={!isValid(validation.attributeName)}
            helperText={getErrors(validation.attributeName)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={<Checkbox checked={engineAttribute.sensitiveInfo || false} />}
            label={t('EngineAttributes.Details.SensitiveInfo')}
            onChange={event => onPropertyChanged('sensitiveInfo', event.target.checked)}
          ></FormControlLabel>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            value={engineAttribute.description || ''}
            onChange={event => onPropertyChanged('description', event)}
            label={t('EngineAttributes.Details.Description')}
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <FormulaFieldLabel error={!isValid(validation.expression)} required>
            {t('EngineAttributes.Details.Expression')}
          </FormulaFieldLabel>
          <FormulaFieldContainer>
            <FormulaField value={engineAttribute.expression} onChange={value => onPropertyChanged('expression', value)} />
          </FormulaFieldContainer>
        </Grid>
        <Grid item xs={12}>
          <Divider />
          <Typography variant='subtitle1'>{t('EngineAttributes.TestAttribute')}</Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Tooltip title={t('EngineScoring.Tooltip.EntityIdentifierForTest')}>
            <TextField
              fullWidth
              value={engineAttribute.entityIdentifierForTest || ''}
              onChange={event => onAttributeTestingFieldsChanged('entityIdentifierForTest', event)}
              label={t('EngineAttributes.Details.EntityIdentifier')}
            />
          </Tooltip>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            fullWidth
            labelKey='name'
            valueKey='engineScoringId'
            value={{ engineScoringId: engineAttribute.scoringGridId, name: engineAttribute.scoringGridName }}
            options={scoringGridsForAttributeTesting}
            onChange={event => {
              onAttributeTestingFieldsChanged('scoringGridId', event !== null ? event.engineScoringId : null);
              onAttributeTestingFieldsChanged('scoringGridName', event !== null ? event.name : null);
            }}
            label={t('EngineAttributes.Details.ScoringGrid')}
          />
        </Grid>
        <Grid container item xs={12} sm={4} justifyContent='end'>
          <Button
            color='primary'
            disabled={!engineAttribute.entityIdentifierForTest || !engineAttribute.scoringGridId}
            onClick={() => onTestAttribute(engineAttribute.id, engineAttribute.scoringGridId, engineAttribute.entityIdentifierForTest)}
          >
            {t('EngineAttributes.TestButton')}
          </Button>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth value={attributeTestResult || ''} disabled={true} label={t('EngineAttributes.Details.TestResult')} />
        </Grid>
      </Grid>
    </>
  );
};

EngineAttributeComponent.propTypes = {
  engineAttribute: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  validation: PropTypes.object.isRequired,
  onTestAttribute: PropTypes.func.isRequired,
  attributeTestResult: PropTypes.string.isRequired,
  scoringGridsForAttributeTesting: PropTypes.array.isRequired
};
export default EngineAttributeComponent;
